import {
	AuthenticationDetails,
	CognitoUser,
	CognitoUserPool,
	type CognitoUserSession,
	type ICognitoStorage,
} from 'amazon-cognito-identity-js'
import { cognitoConfig } from '@/lib/cognito-config'

// Direct Cognito sign-in (ADR-0011 amendment). Collects credentials on Atlas's
// own page and authenticates via `InitiateAuth`/`USER_SRP_AUTH` — the password
// itself never leaves the browser, only SRP proof values do. This is a
// separate API surface from Managed Login's Authorization Code + PKCE flow;
// the resulting tokens are bridged into the SAME oidc-client-ts session store
// `react-oidc-context` reads from (see `direct-session-bridge.ts`), so every
// downstream consumer (`useAuth`, `ProtectedRoute`, logout, silent renew) is
// unaware of which path produced the session.

export interface DirectAuthTokens {
	idToken: string
	accessToken: string
	refreshToken: string
	/** Seconds until the access token expires, from Cognito's own token payload. */
	expiresIn: number
}

export type SignInResult =
	| { kind: 'success'; tokens: DirectAuthTokens }
	| { kind: 'new_password_required'; cognitoUser: CognitoUser }

/** `authority` is `https://cognito-idp.<region>.amazonaws.com/<userPoolId>` — the pool id is its last path segment. */
export function userPoolIdFromAuthority(authority: string): string {
	const id = authority.split('/').pop()
	if (!id) throw new Error(`Cannot extract a Cognito user pool id from authority "${authority}"`)
	return id
}

/**
 * amazon-cognito-identity-js defaults to `window.localStorage` for its own
 * internal token cache whenever no `Storage` override is given — a SECOND,
 * persistent token store this app never reads, entirely outside the
 * sessionStorage-backed oidc-client-ts session `direct-session-bridge.ts`
 * bridges the real tokens into. Passing this in-memory store instead means
 * the SDK still has somewhere to write during the SRP handshake itself, but
 * nothing survives a page reload, and logout (which never clears the SDK's
 * own storage) can't leave a credential-residue window behind.
 */
function memoryCognitoStorage(): ICognitoStorage {
	const store = new Map<string, string>()
	return {
		setItem: (key, value) => store.set(key, value),
		getItem: (key) => store.get(key) ?? null,
		removeItem: (key) => store.delete(key),
		clear: () => store.clear(),
	}
}

function buildUserPool(): CognitoUserPool {
	if (!cognitoConfig) throw new Error('cognito-direct-auth called without a valid Cognito configuration')
	return new CognitoUserPool({
		UserPoolId: userPoolIdFromAuthority(cognitoConfig.authority),
		ClientId: cognitoConfig.clientId,
		Storage: memoryCognitoStorage(),
	})
}

function buildCognitoUser(email: string, pool: CognitoUserPool): CognitoUser {
	return new CognitoUser({ Username: email, Pool: pool, Storage: memoryCognitoStorage() })
}

export function tokensFromSession(session: CognitoUserSession): DirectAuthTokens {
	const accessToken = session.getAccessToken()
	return {
		idToken: session.getIdToken().getJwtToken(),
		accessToken: accessToken.getJwtToken(),
		refreshToken: session.getRefreshToken().getToken(),
		expiresIn: Math.max(0, accessToken.getExpiration() - Math.floor(Date.now() / 1000)),
	}
}

/** Cognito's own error `name` values, mapped to short, safe-to-display copy. Anything unrecognized falls back to `err.message` or a generic string. */
export function describeCognitoError(err: unknown): string {
	const name = (err as { name?: string } | undefined)?.name
	switch (name) {
		case 'NotAuthorizedException':
			return 'Incorrect email or password.'
		case 'UserNotFoundException':
			return 'Incorrect email or password.'
		case 'UserNotConfirmedException':
			return 'This account has not been confirmed yet. Contact an administrator.'
		case 'PasswordResetRequiredException':
			return 'A password reset is required. Use "Forgot?" to set a new password.'
		case 'LimitExceededException':
		case 'TooManyRequestsException':
			return 'Too many attempts. Please wait a moment and try again.'
		case 'CodeMismatchException':
			return 'That code is incorrect.'
		case 'ExpiredCodeException':
			return 'That code has expired. Request a new one.'
		case 'InvalidPasswordException':
			return 'That password does not meet the account requirements.'
		default:
			return (err as { message?: string } | undefined)?.message ?? 'Something went wrong. Please try again.'
	}
}

/** SRP sign-in. Resolves `new_password_required` for a Cognito FORCE_CHANGE_PASSWORD user (every freshly-provisioned account) instead of throwing. */
export function signIn(email: string, password: string): Promise<SignInResult> {
	const pool = buildUserPool()
	const cognitoUser = buildCognitoUser(email, pool)
	const authDetails = new AuthenticationDetails({ Username: email, Password: password })

	return new Promise((resolve, reject) => {
		cognitoUser.authenticateUser(authDetails, {
			onSuccess: (session) => resolve({ kind: 'success', tokens: tokensFromSession(session) }),
			onFailure: (err) => reject(err),
			newPasswordRequired: () => resolve({ kind: 'new_password_required', cognitoUser }),
		})
	})
}

/** Completes a FORCE_CHANGE_PASSWORD challenge for the `cognitoUser` returned by {@link signIn}'s `new_password_required` result. */
export function completeNewPassword(cognitoUser: CognitoUser, newPassword: string): Promise<DirectAuthTokens> {
	return new Promise((resolve, reject) => {
		cognitoUser.completeNewPasswordChallenge(
			newPassword,
			{},
			{
				onSuccess: (session) => resolve(tokensFromSession(session)),
				onFailure: (err) => reject(err),
			},
		)
	})
}

/** Starts Cognito's own forgot-password flow — a verification code is delivered to the account's email. */
export function forgotPassword(email: string): Promise<void> {
	const pool = buildUserPool()
	const cognitoUser = buildCognitoUser(email, pool)
	return new Promise((resolve, reject) => {
		cognitoUser.forgotPassword({
			onSuccess: () => resolve(),
			onFailure: (err) => reject(err),
		})
	})
}

/** Completes the forgot-password flow with the emailed code and a new password. */
export function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
	const pool = buildUserPool()
	const cognitoUser = buildCognitoUser(email, pool)
	return new Promise((resolve, reject) => {
		cognitoUser.confirmPassword(code, newPassword, {
			onSuccess: () => resolve(),
			onFailure: (err) => reject(err),
		})
	})
}
